import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SEOHead from '@/components/seo/SEOHead';
import {
  Truck, Shield, Clock, Users, ArrowRight, Package, UserCheck, LayoutDashboard,
  Star, TrendingUp, MapPin, DollarSign, Check, Zap, Award, Phone, Mail,
  Building, User, ChevronDown, Play, Sparkles, Target, Eye, Heart,
  Network, Globe, BarChart3, ShieldCheck, Wallet, MessageSquare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import useAuthStore from '@/store/authStore';
import SectionDivider from '@/components/ui/section-divider';
import LandingPageWrapper from '@/components/LandingPageWrapper';
import { publicAPI } from '@/services/api';

// ========== HERO SECTION ==========
const HeroSection = () => {
    const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();
    const [currentTextIndex, setCurrentTextIndex] = useState(0);

  const heroContent = [
    {
      title: (
        <>
          Connectez. <span className="text-amber-500">Livrez</span>. Réussissez.
        </>
      ),
      subtitle: "La plateforme intelligente qui révolutionne la logistique en Guinée",
      highlight: "La Guinée en mouvement",
    },
    {
      title: (
        <>
          Votre <span className="text-amber-500">Colis</span>, Notre Mission.
        </>
      ),
      subtitle: "Des milliers de transporteurs à votre service, partout en Guinée",
      highlight: "Transport sécurisé",
    },
    {
      title: (
        <>
          Transporteurs, Multipliez vos <span className="text-amber-500">Revenus</span>.
        </>
      ),
      subtitle: "Accédez à des centaines de missions de livraison chaque jour",
      highlight: "Missions disponibles",
    }
  ];

    useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTextIndex(prev => (prev + 1) % heroContent.length);
        }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden bg-white">
      {/* Floating Bubbles Background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Yellow/Amber Bubbles */}
        <motion.div
          animate={{ y: [0, -30, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-20 right-[15%] w-[400px] h-[400px] bg-gradient-to-br from-amber-200/30 to-amber-300/20 rounded-full filter blur-3xl"
        />

        <motion.div
          animate={{ y: [0, 40, 0], scale: [1, 1.15, 1] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          className="absolute bottom-32 left-[10%] w-[500px] h-[500px] bg-gradient-to-tl from-amber-300/25 to-yellow-200/20 rounded-full filter blur-3xl"
        />

        <motion.div
          animate={{ y: [0, -20, 0], x: [0, 20, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 4 }}
          className="absolute top-[40%] right-[25%] w-[300px] h-[300px] bg-amber-200/20 rounded-full filter blur-2xl"
        />

        {/* Dark Green/Teal Bubbles */}
        <motion.div
          animate={{ y: [0, 35, 0], scale: [1, 1.12, 1] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute top-[15%] left-[20%] w-[450px] h-[450px] bg-gradient-to-br from-[#0d5850]/15 to-teal-700/10 rounded-full filter blur-3xl"
        />

        <motion.div
          animate={{ y: [0, -25, 0], scale: [1, 1.08, 1] }}
          transition={{ duration: 11, repeat: Infinity, ease: "easeInOut", delay: 3 }}
          className="absolute bottom-[20%] right-[20%] w-[380px] h-[380px] bg-gradient-to-tl from-teal-600/12 to-[#0d5850]/15 rounded-full filter blur-3xl"
        />

        <motion.div
          animate={{ y: [0, 30, 0], x: [0, -15, 0] }}
          transition={{ duration: 13, repeat: Infinity, ease: "easeInOut", delay: 5 }}
          className="absolute top-[60%] left-[15%] w-[320px] h-[320px] bg-[#0d5850]/10 rounded-full filter blur-2xl"
        />

        {/* Small accent bubbles */}
        <motion.div
          animate={{ y: [0, -15, 0], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-[30%] right-[40%] w-[200px] h-[200px] bg-amber-300/15 rounded-full filter blur-2xl"
        />

        <motion.div
          animate={{ y: [0, 20, 0], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          className="absolute bottom-[35%] left-[35%] w-[180px] h-[180px] bg-teal-500/12 rounded-full filter blur-2xl"
        />

        {/* Animated logo icons - Subtle */}
        <motion.div
          animate={{ x: ['-10%', '110%'] }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          className="absolute top-32 left-0 opacity-10"
        >
          <img src="/logo.png" alt="LogiConnect" className="h-56 w-auto object-contain" />
        </motion.div>

        <motion.div
          animate={{ x: ['110%', '-10%'] }}
          transition={{ duration: 30, repeat: Infinity, ease: "linear", delay: 8 }}
          className="absolute bottom-40 right-0 opacity-10"
        >
          <img src="/logo.png" alt="LogiConnect" className="h-48 w-auto object-contain transform scale-x-[-1]" />
        </motion.div>
      </div>

      <div className="container mx-auto px-4 py-32 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-100 to-amber-200 border border-amber-300 rounded-full px-4 py-2 text-amber-800 text-sm font-semibold shadow-sm"
            >
              <Sparkles className="w-4 h-4" />
              {heroContent[currentTextIndex].highlight}
            </motion.div>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentTextIndex}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.6 }}
              >
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6 bg-gradient-to-r from-gray-900 via-[#0d5850] to-gray-900 bg-clip-text text-transparent">
                  {heroContent[currentTextIndex].title}
                        </h1>
                <p className="text-xl md:text-2xl text-gray-700 max-w-xl leading-relaxed">
                  {heroContent[currentTextIndex].subtitle}
                        </p>
                    </motion.div>
                </AnimatePresence>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              {isAuthenticated && user ? (
                <Button
                  size="lg"
                  onClick={() => {
                    const roleRoutes = {
                      'ADMIN': '/admin/dashboard',
                      'AGENT': '/admin/dashboard',
                      'CLIENT': '/client/dashboard',
                      'TRANSPORTEUR': '/transporter/dashboard',
                    };
                    const dashboardRoute = roleRoutes[user.role] || '/';
                    navigate(dashboardRoute);
                  }}
                  className="bg-gradient-to-r from-[#0d5850] to-teal-600 hover:from-[#0a4740] hover:to-teal-700 text-white px-8 py-6 text-lg shadow-xl shadow-teal-500/20 transition-all transform hover:scale-105"
                >
                  <LayoutDashboard className="mr-2 w-5 h-5" />
                  Accéder au Tableau de Bord
                </Button>
              ) : (
                <>
                  <Button
                    size="lg"
                    onClick={() => navigate('/auth?tab=register')}
                    className="bg-gradient-to-r from-[#0d5850] to-teal-600 hover:from-[#0a4740] hover:to-teal-700 text-white px-8 py-6 text-lg shadow-xl shadow-teal-500/20 transition-all transform hover:scale-105"
                  >
                    Commencer Gratuitement
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                    className="border-2 border-gray-300 bg-white text-gray-800 hover:bg-gray-50 px-8 py-6 text-lg"
                  >
                    <Play className="mr-2 w-5 h-5" />
                    Comment ça marche
                  </Button>
                </>
              )}
            </motion.div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="flex items-center gap-8 pt-8 border-t border-gray-200"
            >
              <div>
                <div className="text-3xl font-bold bg-gradient-to-r from-[#0d5850] to-teal-600 bg-clip-text text-transparent">500+</div>
                <div className="text-sm text-gray-600 font-medium">Transporteurs actifs</div>
              </div>
              <div>
                <div className="text-3xl font-bold bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">10k+</div>
                <div className="text-sm text-gray-600 font-medium">Livraisons réussies</div>
              </div>
              <div>
                <div className="text-3xl font-bold bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">4.8★</div>
                <div className="text-sm text-gray-600 font-medium">Satisfaction client</div>
              </div>
            </motion.div>
          </div>

          {/* Right Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="relative hidden lg:block"
          >
            <div className="relative w-full h-[520px] rounded-3xl overflow-hidden shadow-2xl ring-1 ring-gray-200">
              <img
                src="https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?q=80&w=2940&auto=format&fit=crop"
                alt="Camion de transport LogiConnect"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d5850]/60 via-transparent to-transparent" />

              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { icon: Shield, label: "Sécurisé", sub: "Suivi en temps réel" },
                    { icon: Zap, label: "Rapide", sub: "Connexion instantanée" },
                    { icon: Award, label: "Fiable", sub: "Transporteurs vérifiés" },
                  ].map((item, i) => (
                    <motion.div
                      key={item.label}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.8 + i * 0.15, duration: 0.5 }}
                      className="bg-white/95 backdrop-blur-sm rounded-xl p-4 text-center shadow-lg"
                    >
                      <item.icon className="w-6 h-6 text-[#0d5850] mx-auto mb-2" />
                      <div className="text-sm font-semibold text-gray-900">{item.label}</div>
                      <div className="text-xs text-gray-500">{item.sub}</div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2 text-gray-400 hover:text-[#0d5850] cursor-pointer transition-colors"
          onClick={() => document.getElementById('who-we-are')?.scrollIntoView({ behavior: 'smooth' })}
        >
          <ChevronDown className="w-8 h-8" />
        </motion.div>
      </div>
    </section>
  );
};

// ========== WHO WE ARE SECTION ==========
const WhoWeAreSection = () => {
  return (
    <section id="who-we-are" className="py-24 px-4 bg-white">
      <div className="container mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
                      <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
          >
            <div className="space-y-6">
              <div className="inline-block">
                <span className="text-[#0d5850] font-semibold text-sm uppercase tracking-wide">Qui Sommes-Nous</span>
                <div className="h-1 w-20 bg-gradient-to-r from-[#0d5850] to-amber-500 mt-2" />
              </div>

              <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
                La Plateforme qui <span className="text-[#0d5850]">Révolutionne</span> la Logistique Guinéenne
              </h2>

              <p className="text-lg text-gray-600 leading-relaxed">
                <strong>LogiConnect</strong> est née d'un constat simple : le transport et la logistique en Guinée
                méritaient une solution moderne, efficace et accessible à tous. Nous avons créé une plateforme
                intelligente qui connecte instantanément ceux qui ont besoin d'expédier avec ceux qui peuvent livrer.
              </p>

              <div className="grid md:grid-cols-3 gap-6 pt-6">
                <div className="text-center p-4">
                  <div className="w-16 h-16 bg-[#0d5850]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Target className="w-8 h-8 text-[#0d5850]" />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">Notre Mission</h4>
                  <p className="text-sm text-gray-600">Démocratiser l'accès à une logistique de qualité</p>
                </div>
                <div className="text-center p-4">
                  <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Eye className="w-8 h-8 text-amber-600" />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">Notre Vision</h4>
                  <p className="text-sm text-gray-600">Devenir le leader de la logistique collaborative</p>
                </div>
                <div className="text-center p-4">
                  <div className="w-16 h-16 bg-teal-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Heart className="w-8 h-8 text-teal-600" />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">Nos Valeurs</h4>
                  <p className="text-sm text-gray-600">Confiance, transparence et innovation</p>
                </div>
              </div>
            </div>
                      </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="grid grid-cols-2 gap-6">
              <img
                src="https://images.unsplash.com/photo-1566492031773-4f4e44671857?q=80&w=800"
                alt="Team"
                className="rounded-2xl shadow-xl w-full h-64 object-cover"
              />
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=800"
                alt="Innovation"
                className="rounded-2xl shadow-xl w-full h-64 object-cover mt-12"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-gradient-to-br from-[#0d5850] to-teal-600 text-white rounded-2xl p-6 shadow-xl max-w-xs">
              <div className="flex items-center gap-4">
                <Award className="w-12 h-12" />
                <div>
                  <div className="text-2xl font-bold">N°1</div>
                  <div className="text-sm opacity-90">En Guinée pour la logistique digitale</div>
                </div>
              </div>
            </div>
          </motion.div>
            </div>
          </div>
        </section>
    );
};

// ========== USER PROFILES SECTION ==========
const UserProfilesSection = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('client');

  const profiles = {
    client: {
      title: "Vous êtes Client",
      subtitle: "Entreprise ou Particulier",
      description: "Vous avez besoin de transporter des marchandises, des colis ou des documents ? Que vous soyez une grande entreprise gérant des volumes importants, une PME cherchant à optimiser sa logistique, ou un particulier souhaitant envoyer un colis à un proche, LogiConnect est fait pour vous.",
      benefits: [
        "Accès instantané à des centaines de transporteurs vérifiés",
        "Comparaison des tarifs et choix du meilleur rapport qualité-prix",
        "Suivi en temps réel de toutes vos expéditions",
        "Paiement sécurisé avec garantie de satisfaction",
        "Support client dédié 7j/7",
        "Historique complet et factures automatiques"
      ],
      icon: Building,
      color: "from-amber-500 to-amber-600",
      bgColor: "bg-amber-50"
    },
    transporter: {
      title: "Vous êtes Transporteur",
      subtitle: "Indépendant ou Entreprise de Transport",
      description: "Vous possédez un véhicule et cherchez à optimiser vos trajets ? Vous êtes chauffeur indépendant, une entreprise de transport établie, ou vous souhaitez simplement rentabiliser vos déplacements quotidiens ? LogiConnect vous connecte avec des clients en attente de vos services.",
      benefits: [
        "Accès à des centaines de missions chaque jour",
        "Liberté totale : choisissez vos missions et vos horaires",
        "Paiements rapides et sécurisés après chaque livraison",
        "Optimisation de vos trajets pour maximiser vos revenus",
        "Assurance professionnelle incluse",
        "Outils de gestion et statistiques de performance"
      ],
      icon: User,
      color: "from-[#0d5850] to-teal-700",
      bgColor: "bg-teal-50"
    }
  };

  const currentProfile = profiles[activeTab];

  return (
    <section className="py-24 px-4 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#0d5850] font-semibold text-sm uppercase tracking-wide">Pour Qui ?</span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-4 mb-6">
            Une Solution pour <span className="text-[#0d5850]">Chaque Profil</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Que vous cherchiez à expédier ou à livrer, LogiConnect s'adapte à vos besoins spécifiques
          </p>
        </motion.div>

        {/* Tab Selector */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-white rounded-full p-2 shadow-lg">
            <button
              onClick={() => setActiveTab('client')}
              className={`px-8 py-3 rounded-full font-semibold transition-all ${
                activeTab === 'client'
                  ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-lg shadow-amber-500/30'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Building className="w-5 h-5 inline-block mr-2" />
              Je suis Client
            </button>
            <button
              onClick={() => setActiveTab('transporter')}
              className={`px-8 py-3 rounded-full font-semibold transition-all ${
                activeTab === 'transporter'
                  ? 'bg-gradient-to-r from-[#0d5850] to-teal-700 text-white shadow-lg shadow-teal-500/30'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <img src="/logo.png" alt="LogiConnect" className="h-8 w-auto inline-block mr-2 object-contain" />
              Je suis Transporteur
            </button>
          </div>
        </div>

        {/* Profile Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className={`${currentProfile.bgColor} rounded-3xl p-8 md:p-12 shadow-xl`}
          >
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className={`w-16 h-16 bg-gradient-to-br ${currentProfile.color} rounded-2xl flex items-center justify-center mb-6 shadow-lg`}>
                  <currentProfile.icon className="w-8 h-8 text-white" />
                </div>

                <h3 className="text-3xl font-bold text-gray-900 mb-2">{currentProfile.title}</h3>
                <p className="text-xl text-gray-600 mb-6">{currentProfile.subtitle}</p>
                <p className="text-lg text-gray-700 leading-relaxed mb-8">{currentProfile.description}</p>

                <Button
                  size="lg"
                  onClick={() => navigate('/auth/register')}
                  className={`bg-gradient-to-r ${currentProfile.color} text-white px-8 py-6 text-lg shadow-xl hover:scale-105 transition-transform`}
                >
                  Rejoindre maintenant
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h4 className="text-2xl font-bold text-gray-900 mb-6">Vos Avantages</h4>
                <ul className="space-y-4">
                  {currentProfile.benefits.map((benefit, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="flex items-start gap-3"
                    >
                      <div className={`w-6 h-6 bg-gradient-to-br ${currentProfile.color} rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-gray-700">{benefit}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

// ========== HOW IT WORKS - ANIMATED CONNECTION ==========
const HowItWorksSection = () => {
  return (
    <section id="how-it-works" className="py-24 px-4 bg-gradient-to-br from-[#0a1e1b] via-[#0d2d28] to-[#0a1e1b] text-white relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-10 w-64 h-64 bg-[#0d5850]/20 rounded-full filter blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-10 w-80 h-80 bg-amber-500/10 rounded-full filter blur-3xl animate-pulse animation-delay-2000" />
      </div>

      <div className="container mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <span className="text-amber-400 font-semibold text-sm uppercase tracking-wide">Comment Ça Marche</span>
          <h2 className="text-4xl md:text-5xl font-bold mt-4 mb-6">
            Une <span className="text-amber-400">Connexion Intelligente</span> en 3 Étapes
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Notre technologie connecte instantanément les besoins avec les solutions
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-12 mb-20">
          {[
            {
              step: "01",
              title: "Publication de la Mission",
              description: "Le client publie sa demande avec tous les détails : origine, destination, type de marchandise, délai souhaité.",
              icon: Package,
              color: "from-blue-500 to-blue-700"
            },
            {
              step: "02",
              title: "Matching Intelligent",
              description: "Notre algorithme analyse et notifie instantanément les transporteurs pertinents selon leur localisation et disponibilité.",
              icon: Network,
              color: "from-amber-500 to-amber-700"
            },
            {
              step: "03",
              title: "Connexion & Livraison",
              description: "Le client choisit son transporteur, le paiement est sécurisé, et la livraison est suivie en temps réel jusqu'à sa finalisation.",
              icon: ShieldCheck,
              color: "from-[#0d5850] to-teal-700"
            }
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="relative"
            >
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all group h-full">
                <div className={`w-16 h-16 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center mb-6 shadow-xl group-hover:scale-110 transition-transform`}>
                  <item.icon className="w-8 h-8 text-white" />
                </div>
                <div className="text-5xl font-bold text-white/10 mb-4">{item.step}</div>
                <h3 className="text-2xl font-bold mb-4">{item.title}</h3>
                <p className="text-gray-300 leading-relaxed">{item.description}</p>
              </div>
              {index < 2 && (
                <div className="hidden lg:block absolute top-1/2 -right-6 transform -translate-y-1/2">
                  <ArrowRight className="w-8 h-8 text-amber-400" />
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Animated Process Visualization */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-12 relative overflow-hidden"
        >
          <div className="grid md:grid-cols-3 gap-8 items-center">
            <div className="text-center">
              <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-4 shadow-xl">
                <Building className="w-12 h-12 text-white" />
              </div>
              <h4 className="text-xl font-bold mb-2">Client</h4>
              <p className="text-gray-400 text-sm">Publie une mission</p>
            </div>

            <div className="relative">
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="w-32 h-32 bg-gradient-to-br from-amber-500 to-amber-700 rounded-full flex items-center justify-center mx-auto shadow-2xl"
              >
                <Network className="w-16 h-16 text-white" />
              </motion.div>
              <div className="absolute top-1/2 left-0 w-full h-0.5 bg-gradient-to-r from-blue-500 via-amber-500 to-teal-500 -z-10" />
              <motion.div
                animate={{ x: ['-100%', '100%'] }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                className="absolute top-1/2 left-0 w-4 h-4 bg-white rounded-full shadow-lg"
              />
            </div>

            <div className="text-center">
              <img src="/logo.png" alt="LogiConnect" className="h-40 w-auto object-contain mx-auto mb-4" />
              <h4 className="text-xl font-bold mb-2">Transporteur</h4>
              <p className="text-gray-400 text-sm">Accepte et livre</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ========== STATISTICS SECTION ==========
const StatisticsSection = () => {
  const stats = [
    { value: "500+", label: "Transporteurs Actifs", icon: "logo", color: "from-blue-500 to-blue-700" },
    { value: "10,000+", label: "Livraisons Réussies", icon: Package, color: "from-[#0d5850] to-teal-700" },
    { value: "1,200+", label: "Clients Satisfaits", icon: Users, color: "from-amber-500 to-amber-700" },
    { value: "98%", label: "Taux de Satisfaction", icon: Star, color: "from-green-500 to-green-700" },
    ];

    return (
    <section className="py-24 px-4 bg-white">
          <div className="container mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
          <span className="text-[#0d5850] font-semibold text-sm uppercase tracking-wide">Nos Chiffres</span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-4 mb-6">
            Des Résultats qui <span className="text-[#0d5850]">Parlent d'Eux-Mêmes</span>
              </h2>
            </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="relative group"
            >
              <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-8 text-center hover:shadow-2xl transition-all group-hover:-translate-y-2">
                <div className={`w-16 h-16 bg-gradient-to-br ${stat.color} rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform`}>
                  {stat.icon === "logo" ? (
                    <img src="/logo.png" alt="LogiConnect" className="h-12 w-auto object-contain" />
                  ) : (
                    <stat.icon className="w-8 h-8 text-white" />
                  )}
                </div>
                <div className="text-5xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
    );
};

// ========== FEATURES SECTION ==========
const FeaturesSection = () => {
  const features = [
    { icon: Shield, title: "Sécurité Maximale", description: "Vérification complète des transporteurs et assurance pour chaque livraison", color: "from-green-500 to-green-700" },
    { icon: Zap, title: "Réponse Instantanée", description: "Recevez des offres en quelques minutes après publication de votre mission", color: "from-amber-500 to-amber-700" },
    { icon: MapPin, title: "Suivi en Temps Réel", description: "Localisez votre colis à chaque instant avec notre système GPS intégré", color: "from-blue-500 to-blue-700" },
    { icon: Wallet, title: "Paiement Sécurisé", description: "Transactions protégées avec plusieurs moyens de paiement disponibles", color: "from-[#0d5850] to-teal-700" },
    { icon: BarChart3, title: "Tableaux de Bord", description: "Statistiques détaillées et historique complet de toutes vos opérations", color: "from-purple-500 to-purple-700" },
    { icon: MessageSquare, title: "Support 7j/7", description: "Une équipe dédiée disponible pour vous accompagner à tout moment", color: "from-pink-500 to-pink-700" },
    ];

    return (
    <section id="features" className="py-24 px-4 bg-gradient-to-br from-gray-50 to-white">
            <div className="container mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
          <span className="text-[#0d5850] font-semibold text-sm uppercase tracking-wide">Fonctionnalités</span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-4 mb-6">
            Tout ce dont vous avez <span className="text-[#0d5850]">Besoin</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Une plateforme complète avec tous les outils pour une logistique efficace
                    </p>
                </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
                                <motion.div
                                    key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group"
            >
              <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-2xl transition-all group-hover:-translate-y-2 h-full">
                <div className={`w-14 h-14 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform`}>
                  <feature.icon className="w-7 h-7 text-white" />
                                    </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                                    </div>
                                </motion.div>
                            ))}
                </div>
            </div>
        </section>
    );
};

// ========== TESTIMONIALS SECTION ==========

const FALLBACK_TESTIMONIALS = [
  {
    id: 1,
    author_name: "Etienne Kamano",
    author_role: "Directeur Commercial, Import Plus SARL",
    content: "LogiConnect a transformé notre gestion logistique. Nous réduisons nos coûts de 30% tout en améliorant nos délais de livraison. L'équipe est réactive et professionnelle.",
    rating: 5,
    author_avatar_url: "https://i.pravatar.cc/150?u=etienne",
  },
  {
    id: 2,
    author_name: "Joseph Keloua",
    author_role: "Transporteur Indépendant",
    content: "Grâce à LogiConnect, je gagne 3x plus qu'avant. La plateforme est facile à utiliser et les paiements sont toujours à l'heure. Je recommande à tous les transporteurs !",
    rating: 5,
    author_avatar_url: "https://i.pravatar.cc/150?u=joseph",
  },
  {
    id: 3,
    author_name: "Timothée Iffono",
    author_role: "Gérant, Électro Conakry",
    content: "En tant que commerçant, j'ai besoin de fiabilité. LogiConnect me garantit des livraisons rapides et sécurisées pour mes équipements électroniques. Service impeccable.",
    rating: 5,
    author_avatar_url: "https://i.pravatar.cc/150?u=timothee",
  },
];

const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState(FALLBACK_TESTIMONIALS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicAPI
      .getTestimonials()
      .then((res) => {
        const data = res.data?.results ?? res.data;
        if (Array.isArray(data) && data.length > 0) setTestimonials(data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <section id="testimonials" className="py-24 px-4 bg-white">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#0d5850] font-semibold text-sm uppercase tracking-wide">Témoignages</span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-4 mb-6">
            Ils nous font <span className="text-[#0d5850]">Confiance</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Découvrez ce que nos utilisateurs pensent de LogiConnect
          </p>
        </motion.div>

        {loading ? (
          <div className="grid md:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse rounded-xl border border-gray-200 p-8 space-y-4">
                <div className="flex gap-1">
                  {[...Array(5)].map((_, j) => (
                    <div key={j} className="w-5 h-5 bg-gray-200 rounded" />
                  ))}
                </div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-full" />
                  <div className="h-4 bg-gray-200 rounded w-5/6" />
                  <div className="h-4 bg-gray-200 rounded w-4/6" />
                </div>
                <div className="flex items-center gap-4 border-t border-gray-200 pt-6">
                  <div className="w-14 h-14 bg-gray-200 rounded-full" />
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-24" />
                    <div className="h-3 bg-gray-200 rounded w-32" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, index) => (
              <motion.div
                key={t.id || index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full bg-gradient-to-br from-gray-50 to-white border-gray-200 hover:shadow-2xl transition-all hover:-translate-y-2">
                  <CardContent className="p-8">
                    <div className="flex gap-1 mb-4">
                      {[...Array(t.rating || 5)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-amber-500 text-amber-500" />
                      ))}
                    </div>
                    <p className="text-gray-700 italic mb-6 leading-relaxed">"{t.content}"</p>
                    <div className="flex items-center gap-4 border-t border-gray-200 pt-6">
                      {t.author_avatar_url ? (
                        <img
                          src={t.author_avatar_url}
                          alt={t.author_name}
                          className="w-14 h-14 rounded-full object-cover border-2 border-[#0d5850]"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#0d5850] to-teal-600 flex items-center justify-center text-white text-lg font-bold">
                          {(t.author_name || "?")[0]}
                        </div>
                      )}
                      <div>
                        <h4 className="font-bold text-gray-900">{t.author_name}</h4>
                        <p className="text-sm text-gray-600">{t.author_role}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// ========== FAQ SECTION ==========
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "Comment fonctionne LogiConnect ?",
      answer: "LogiConnect est une plateforme qui connecte les personnes ayant besoin d'expédier des colis avec des transporteurs disponibles. Vous publiez votre mission, recevez des offres, choisissez votre transporteur et suivez votre livraison en temps réel."
    },
    {
      question: "Comment sont vérifiés les transporteurs ?",
      answer: "Tous nos transporteurs passent par un processus de vérification rigoureux incluant la validation de leur identité, permis de conduire, documents du véhicule et antécédents. Seuls les transporteurs approuvés peuvent accepter des missions."
    },
    {
      question: "Quels sont les modes de paiement acceptés ?",
      answer: "Nous acceptons les paiements par Mobile Money (Orange Money, MTN Money), carte bancaire, et virement bancaire. Tous les paiements sont sécurisés et traités via notre plateforme."
    },
    {
      question: "Que se passe-t-il en cas de problème avec une livraison ?",
      answer: "Chaque livraison est assurée. En cas de problème, contactez immédiatement notre support client disponible 7j/7. Nous intervenons rapidement pour résoudre tout litige et garantir votre satisfaction."
    },
    {
      question: "Comment devenir transporteur sur LogiConnect ?",
      answer: "Inscrivez-vous en tant que transporteur, soumettez vos documents (permis, carte grise, assurance), attendez la validation (24-48h), puis commencez à accepter des missions. C'est simple et rapide !"
    },
    {
      question: "Y a-t-il des frais d'inscription ?",
      answer: "L'inscription est entièrement gratuite pour les clients et les transporteurs. Nous ne prélevons qu'une petite commission sur les transactions réussies pour maintenir et améliorer la plateforme."
    },
  ];

  return (
    <section className="py-24 px-4 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#0d5850] font-semibold text-sm uppercase tracking-wide">FAQ</span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-4 mb-6">
            Questions <span className="text-[#0d5850]">Fréquentes</span>
          </h2>
          <p className="text-xl text-gray-600">
            Tout ce que vous devez savoir sur LogiConnect
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-all"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <span className="font-semibold text-gray-900 pr-8">{faq.question}</span>
                <ChevronDown
                  className={`w-5 h-5 text-[#0d5850] flex-shrink-0 transition-transform ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-5 text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ========== CTA SECTION ==========
const CtaSection = () => {
    const navigate = useNavigate();

    return (
    <section className="py-24 px-4 bg-gradient-to-br from-[#0a1e1b] via-[#0d2d28] to-[#0a1e1b] relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute top-10 left-10 w-96 h-96 bg-[#0d5850]/20 rounded-full filter blur-3xl animate-pulse" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-amber-500/10 rounded-full filter blur-3xl animate-pulse animation-delay-2000" />
      </div>

      <div className="container mx-auto relative z-10">
             <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
                transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto text-center text-white"
        >
          <div className="mb-8">
            <span className="inline-block px-4 py-2 bg-amber-500/20 border border-amber-500/30 rounded-full text-amber-400 text-sm font-semibold mb-6">
              <Sparkles className="w-4 h-4 inline-block mr-2" />
              Rejoignez la Révolution Logistique
            </span>
          </div>

          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            Prêt à Transformer Votre <span className="text-amber-400">Logistique</span> ?
          </h2>
          <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto leading-relaxed">
            Des milliers d'utilisateurs nous font déjà confiance. Rejoignez LogiConnect aujourd'hui
            et découvrez une nouvelle façon de gérer vos expéditions.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-12">
              <Button
                size="lg"
              onClick={() => navigate('/auth/register')}
              className="bg-gradient-to-r from-[#0d5850] to-teal-600 hover:from-[#0a4740] hover:to-teal-700 text-white px-10 py-7 text-lg shadow-2xl hover:scale-105 transition-transform"
              >
              Créer mon compte gratuitement
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/auth?tab=login')}
              className="border-2 border-white/30 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 px-10 py-7 text-lg"
            >
              Se connecter
            </Button>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-gray-400">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-400" />
              Inscription gratuite
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-400" />
              Aucune carte requise
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-400" />
              Support 7j/7
            </div>
          </div>
            </motion.div>
          </div>
        </section>
    );
};

// ========== MAIN LANDING PAGE COMPONENT ==========
const LandingPage = () => {
  return (
    <LandingPageWrapper>
      <div className="w-full overflow-x-hidden">
        <SEOHead
          title="Votre Partenaire Logistique en Guinée | Transport Intelligent"
          description="La plateforme N°1 pour connecter clients et transporteurs en Guinée. Transport sécurisé, suivi en temps réel, paiement sécurisé. Rejoignez des milliers d'utilisateurs satisfaits."
          keywords="logistique, transport, Guinée, livraison, transporteur, colis, fret, Conakry, logistique Guinée, transport sécurisé"
          url="/"
        />

        <HeroSection />
        <SectionDivider variant="diamond" color="teal" />

        <WhoWeAreSection />
        <SectionDivider variant="dots" color="amber" />

        <UserProfilesSection />
        <SectionDivider variant="line" color="teal" />

        <HowItWorksSection />
        <SectionDivider variant="dots" color="amber" />

        <StatisticsSection />
        <SectionDivider variant="spacer" className="py-4" />

        <FeaturesSection />
        <SectionDivider variant="diamond" color="teal" />

        <TestimonialsSection />
        <SectionDivider variant="line" color="amber" />

        <FAQSection />
        <SectionDivider variant="dots" color="teal" />

        <CtaSection />
      </div>
    </LandingPageWrapper>
  );
};

export default LandingPage;
